import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogCancelButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { Textarea } from '@/components/ui-kit/textarea';
import { dateOnlyToUtcIso, orgCalendarDate } from '@/lib/utils/custom-date/custom-date';
import { translatedZodResolver } from '@/lib/utils/validation/translated-zod-resolver';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestToil } from '../../hooks/use-toil';
import {
  toilRequestFormSchema,
  ToilRequestFormValues,
} from '../../schemas/toil-request-form-schema';
import { isPastToilDate } from '../../services/toil-eligibility.service';

export interface ToilRequestFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ToilRequestFormDialog = ({ open, onOpenChange }: ToilRequestFormDialogProps) => {
  const { t } = useTranslation();
  const { mutateAsync: requestToil, isPending } = useRequestToil();

  const form = useForm<ToilRequestFormValues>({
    resolver: translatedZodResolver(toilRequestFormSchema, t),
    defaultValues: { ToilType: 'FullDay', ToilDate: '', Reason: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ ToilType: 'FullDay', ToilDate: '', Reason: '' });
  }, [open, form]);

  /**
   * The past-date check runs in the browser; then the request goes straight to the TOIL_REQUEST
   * workflow, which checks for a duplicate and the work-day rule and inserts it. Its answer is shown
   * as a toast by `useRequestToil`.
   */
  const handleSubmit = async (values: ToilRequestFormValues) => {
    if (isPastToilDate(values.ToilDate)) {
      form.setError('ToilDate', { message: t('TOIL_DATE_CANNOT_BE_PAST') });
      return;
    }

    try {
      const result = await requestToil({
        // The picked day as a date-only value (`YYYY-MM-DDT00:00:00.000Z`); stored as `ToilDate`.
        toilDate: dateOnlyToUtcIso(values.ToilDate),
        isHalfDay: values.ToilType === 'HalfDay',
        reason: values.Reason.trim(),
      });
      if (result.IsSuccessful) onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('REQUEST_TOIL')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="ToilType"
              render={({ field }) => (
                // The options beside the title, as in the leave and correction forms.
                <FormItem className="flex flex-wrap items-center gap-x-4 gap-y-2 space-y-0">
                  <FormLabel className="whitespace-nowrap">{t('DURATION')}:</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-wrap gap-x-4 gap-y-2"
                    >
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <RadioGroupItem value="FullDay" className="cursor-pointer" />
                        {t('FULL_DAY')}
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <RadioGroupItem value="HalfDay" className="cursor-pointer" />
                        {t('HALF_DAY')}
                      </label>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage className="basis-full" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ToilDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DATE')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      min={orgCalendarDate(new Date().toISOString())}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('REASON')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} maxLength={100} placeholder={t('REASON')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('SUBMIT')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
